import type { AppEventMap } from "./events/catalog";

const REFRESH_TAGS_HEADER = "X-WMU-Refresh-Tags";
const REFRESH_TAGS_META_KEY = "refresh_tags";

declare global {
  interface Window {
    __WMU_REFRESH_TAGS_BOOTED?: boolean;
  }
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function emitRefreshEvent(detail: AppEventMap["wmu:refresh-tags"]): void {
  if (window.WMUEvents) {
    window.WMUEvents.emit("wmu:refresh-tags", detail);
    return;
  }
  document.dispatchEvent(new CustomEvent("wmu:refresh-tags", { detail, bubbles: true }));
}

export function normalizeRefreshTags(value: unknown): string[] {
  const parts = Array.isArray(value)
    ? value.map((item) => String(item || ""))
    : [String(value || "")];
  const seen = new Set<string>();
  const tags: string[] = [];
  parts
    .flatMap((item) => item.split(/[,\s]+/g))
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      if (seen.has(item)) {
        return;
      }
      seen.add(item);
      tags.push(item);
    });
  return tags;
}

function refreshTargets(tag: string): void {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-refresh-on]")).filter((node) => {
    return normalizeRefreshTags(node.getAttribute("data-refresh-on")).includes(tag);
  });
  nodes.forEach((node) => {
    const url = String(node.getAttribute("data-refresh-url") || node.getAttribute("hx-get") || "").trim();
    if (!url || !window.htmx) return;
    const target = node.id ? `#${node.id}` : node;
    const swap = String(node.getAttribute("data-refresh-swap") || "innerHTML").trim() || "innerHTML";
    window.htmx.ajax("GET", url, { target, swap });
  });
}

export function dispatchRefreshTags(detail: Partial<AppEventMap["wmu:refresh-tags"]>): string[] {
  const tags = normalizeRefreshTags(detail.tags);
  if (tags.length === 0) {
    return [];
  }
  emitRefreshEvent({
    tags,
    reason: typeof detail.reason === "string" ? detail.reason : undefined,
    source: typeof detail.source === "string" ? detail.source : undefined,
    topic: typeof detail.topic === "string" ? detail.topic : undefined,
  });
  return tags;
}

export function extractRefreshTagsFromResponse(response: Response, payload?: unknown): string[] {
  const fromHeader = normalizeRefreshTags(response.headers.get(REFRESH_TAGS_HEADER) || "");
  if (fromHeader.length > 0) {
    return fromHeader;
  }
  if (!isRecord(payload)) {
    return [];
  }
  const meta = payload.meta;
  if (!isRecord(meta)) {
    return [];
  }
  return normalizeRefreshTags(meta[REFRESH_TAGS_META_KEY]);
}

export function dispatchRefreshTagsFromResponse(
  response: Response,
  payload?: unknown,
  detail: Partial<AppEventMap["wmu:refresh-tags"]> = {},
): string[] {
  const tags = extractRefreshTagsFromResponse(response, payload);
  if (tags.length === 0) {
    return [];
  }
  return dispatchRefreshTags({
    ...detail,
    tags,
  });
}

export function installRefreshTagRuntime(): void {
  if (window.__WMU_REFRESH_TAGS_BOOTED) {
    return;
  }
  window.__WMU_REFRESH_TAGS_BOOTED = true;
  document.addEventListener("wmu:refresh-tags", (event: Event) => {
    const detail = (event as CustomEvent<AppEventMap["wmu:refresh-tags"]>).detail;
    normalizeRefreshTags(detail?.tags).forEach((tag) => {
      refreshTargets(tag);
    });
  });
}
